/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { forwardRef, useId } from 'react';

import { useIntl } from 'react-intl';

import { css, jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { SearchTrigger } from '@atlassian/csm-widget-ui-components/main';
import { SearchTriggerInputElement } from '@atlassian/csm-widget-ui-components/search-trigger-input-element';
import { SearchTriggerSubmitButton } from '@atlassian/csm-widget-ui-components/search-trigger-submit-button';

import { messages } from './messages';

type AiSearchInputProps = {
	className?: string;
	hidden?: boolean;
	onChange: (value: string) => void;
	onSubmit: ({ message }: { message: string }) => void;
	value: string;
};

const formStyles = css({
	display: 'flex',
	alignItems: 'center',
	width: 'fit-content',
	willChange: 'opacity, visibility, transform',
	transition: 'opacity 0.2s ease-out, visibility 0.2s ease-out, transform 0.2s ease-out',
	backfaceVisibility: 'hidden',
	transform: 'translate3d(0, 0, 0) perspective(1px)',
});

const hiddenStyles = css({
	pointerEvents: 'none',
	opacity: 0,
	visibility: 'hidden',
});

/**
 * Primitive "dumb" UI component
 * @public
 */
export const AiSearchInput: React.ForwardRefExoticComponent<
	AiSearchInputProps & React.RefAttributes<HTMLFormElement>
> = forwardRef<HTMLFormElement, AiSearchInputProps>(
	({ className, hidden, onChange, onSubmit, value }, ref) => {
		const { formatMessage } = useIntl();
		const id = useId();
		const formLabelledById = `form-${id}-description`;

		const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const formData = new FormData(e.currentTarget);
			const message = formData.get('message');
			if (typeof message !== 'string') {
				throw new Error('Message is not a string');
			}
			onSubmit({ message });
		};

		return (
			<form
				ref={ref}
				aria-hidden={hidden}
				css={[formStyles, hidden && hiddenStyles]}
				// eslint-disable-next-line
				className={className}
				aria-labelledby={formLabelledById}
				onSubmit={handleSubmit}
			>
				<VisuallyHidden>
					<Text id={formLabelledById}>{formatMessage(messages.searchFieldDescription)}</Text>
				</VisuallyHidden>
				<SearchTrigger
					value={value}
					onChange={onChange}
					placeholder={<label htmlFor={id}>{formatMessage(messages.searchPlaceholder)}</label>}
					submitButton={<SearchTriggerSubmitButton as="button" type="submit" />}
				>
					<SearchTriggerInputElement
						id={id}
						inputLabel={formatMessage(messages.searchInputLabel)}
					/>
				</SearchTrigger>
			</form>
		);
	},
);
